import React from 'react';
import { useMutation } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useFormContext } from 'react-hook-form';
import { CREATE_SOLICITATION } from '../queries';
import { pageErrorNotifications } from '../../../utilities/error-handling';
import DuplicateUpiidRenewalAlert from '../components/upiid-details/duplicate-upiid-renewal-alert';
import UpiidDetailsForm from '../components/upiid-details/upiid-details-form';
import solicitationNotifications from '../utils/solicitation-notification-helpers';
import '../components/upiid-details/solicitation-upiid-details.scss';
import { getUpiidDetailsNextPath } from '../utils/solicitation-helpers';
import SolicitationStepper from '../components/solicitation-stepper';
import { useUpiidDetails } from '../components/upiid-details/upiid-details-provider';
import { TO_CONTINUE_FIELD } from '../constants';

const UPIID_EXISTS_ACROSS_PREVIOUS_YEAR = 'uPIID_EXISTS_ACROSS_PREVIOUS_YEAR';
const UPIID_DRAFT_EXISTS_PREVIOUS_YEAR = 'UPIID_DRAFT_EXISTS_PREVIOUS_YEAR';

const CreateSolicitationForm = () => {
  const navigate = useNavigate();
  const { setError } = useFormContext();
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );
  const { bidType } = useUpiidDetails();

  const [createDraftSolicitation] = useMutation(CREATE_SOLICITATION, {
    onError: (requestError) => {
      // the structured solicitation errors arrive as JSON in `message`;
      // anything else is shown as-is
      let errorName;
      let message;
      let data;
      try {
        ({ errorName, message, data } = JSON.parse(requestError.message));
      } catch {
        message = requestError.message;
      }
      let errorMsg = message;

      if (errorName === UPIID_EXISTS_ACROSS_PREVIOUS_YEAR) {
        errorMsg = (
          <DuplicateUpiidRenewalAlert solicitationObj={data.solicitationObj} />
        );
        // set form inputs in error state
        setError('upiidAACRenewal', { type: 'custom' });
        setError('upiidSQRenewal', { type: 'custom' });
      } else if (errorName === UPIID_DRAFT_EXISTS_PREVIOUS_YEAR) {
        errorMsg = (
          <>
            <h3 className="usa-alert__heading margin-right-2">
              uPIID already exists
            </h3>
            <p>
              There is already a solicitation with this uPIID. Please return to
              the{' '}
              <Link to="/catalog/solicitations" className="text-primary">
                Solicitation listing
              </Link>{' '}
              page and filter to the uPIID.
            </p>
          </>
        );
      }

      setSolicitationNotification(
        pageErrorNotifications(requestError, {
          id: 'CREATE_SOLICITATION_ERROR',
          message: errorMsg,
          type: 'error',
          closeable: true,
          showInModal: false,
        }),
      );
    },
  });

  const onSubmit = (data) => {
    void createDraftSolicitation({
      variables: {
        solicitationinput: {
          solicitationNumber: (data.upiidAAC + data.upiidSQ).toUpperCase(),
          title: data.solicitationTitle,
          contractYear: Number(data.contractYear),
          purchaseTypeCode: data.purchaseType,
          solicitationType: data.solicitationProcess,
          description: data.solicitationDescription,
          status: 'Draft',
          samUrl: data.solicitationPosting,
          bidType: data.bidType,
        },
      },
      onCompleted: (response) => {
        const { id, solicitationNumber, bidType } = response.createSolicitation;
        setSolicitationNotification([
          {
            id: 'CREATE_SOLICITATION',
            message: `Solicitation ${solicitationNumber} was successfully added.`,
            type: 'success',
            closeable: true,
            showInModal: false,
            showOnNextPage: true,
          },
        ]);
        navigate(
          getUpiidDetailsNextPath(bidType, id, !data[TO_CONTINUE_FIELD]),
        );
      },
    });
  };

  return (
    <>
      <SolicitationStepper currentStep={1} currentBidType={bidType} />
      <UpiidDetailsForm formId="new-solicitation-form" onSubmit={onSubmit} />
    </>
  );
};

export default CreateSolicitationForm;
